import { HoverCard } from 'radix-ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AgentOrb } from '@/components/ui/agent-orb';
import { relTime, usd } from '@/lib/format';
import { type AgentLive, elapsed, isWaitingRun, useNow } from '@/lib/live-agents';
import { cn } from '@/lib/utils';
import { LiveStatusLabel, useRunTitles } from './AgentsLive';
import { activityText } from './LiveOverview';

/**
 * Hover (or keyboard focus) over an agent anywhere on the company page: what it is doing right now — its
 * status, the task or chat, its latest step, how long and what it cost.
 */
export function AgentHoverCard({ live, children }: { live: AgentLive; children: ReactNode }) {
  return (
    <HoverCard.Root openDelay={250} closeDelay={80}>
      <HoverCard.Trigger asChild>{children}</HoverCard.Trigger>
      <HoverCard.Portal>
        <HoverCard.Content
          side="right"
          align="start"
          sideOffset={8}
          collisionPadding={12}
          className="z-50 w-[min(20rem,calc(100vw-24px))] rounded-xl border border-border bg-panel p-3 text-sm text-fg shadow-pop"
        >
          <AgentNow live={live} />
        </HoverCard.Content>
      </HoverCard.Portal>
    </HoverCard.Root>
  );
}

/** The card's body; also the "now working" block of the agent panel. */
export function AgentNow({ live, header = true }: { live: AgentLive; header?: boolean }) {
  const { t } = useTranslation();
  const titleOf = useRunTitles();
  const { agent, status, run, snippet } = live;
  const active = !!run && (status === 'running' || status === 'approval');
  const busy = active && !isWaitingRun(run);
  const now = useNow(1000, active);
  return (
    <div className="flex flex-col gap-2">
      {header ? (
        <div className="flex items-center gap-2">
          <AgentOrb seed={agent.id} size={24} avatar={agent.avatar} name={agent.name} />
          <span className="min-w-0">
            <span className="block truncate font-medium">{agent.name}</span>
            <span className="block truncate text-xs text-muted">{agent.roleTitle}</span>
          </span>
          <span className="ml-auto">
            <LiveStatusLabel
              status={status}
              since={active && run ? elapsed(run.startedAt ?? run.createdAt, now) : null}
            />
          </span>
        </div>
      ) : null}
      {active && run ? (
        <div className={cn('rounded-lg border bg-bg p-2.5', busy ? 'border-claude/40' : 'border-border')}>
          <span className="block truncate text-xs text-muted">{titleOf(run)}</span>
          <span className={cn('block text-sm', busy ? 'text-fg' : 'text-fg-2')}>{activityText(t, live)}</span>
          {busy && snippet ? (
            <span className="mt-1 line-clamp-2 block font-mono text-xs break-all text-claude">{snippet}</span>
          ) : null}
          <span className="mt-1 block text-2xs text-faint">
            {t('hoverCard.steps', { count: run.stepCount })} · {usd(run.costUsd, 2)}
          </span>
        </div>
      ) : run ? (
        <p className="text-xs text-muted">
          {t('ops.lastRun', { when: relTime(run.endedAt ?? run.createdAt) })} · {titleOf(run)}
        </p>
      ) : (
        <p className="text-xs text-muted">{t('ops.noRun')}</p>
      )}
    </div>
  );
}
